import { Container, Divider } from "@material-ui/core";
import { Box, Grid, Typography } from "@mui/material";
import axios from "axios";
import {
  ArrayChip,
  CurrencyDisplay,
  CustomTable,
  FormCard,
  FormLabel,
  ModalError,
  Page,
  PrimaryButton,
  SectionLabel,
  SkeletonComponent,
  StatusChip,
} from "components";
import html2canvas from "html2canvas";
import { InnoImage } from "inno-ui";
import { jsPDF } from "jspdf";
import { HeaderTitle } from "layouts";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { getErrors, textCapitalization } from "utils";

const DetailAdvancedFinance = () => {
  const classes = globalStyles();
  const inputRef = useRef(null);
  const { id } = useParams();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingButton, setLoadingButton] = useState(false);
  const [pageDetail, setPageDetail] = useState({});
  const [currencyPrefix, setCurrencyPrefix] = useState("Rp");

  const handlePrintPDF = async () => {
    setLoadingButton(true);
    await html2canvas(inputRef.current, { useCORS: true }).then(canvas => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "px", "a4");
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save("download.pdf");
    });
    setLoadingButton(false);
  };

  const getDetail = async () => {
    try {
      const res = await axios.get(
        `${hardBaseUrl}/publisher/transaction/detail/${id}`,
        { headers }
      );
      const { data } = res?.data;
      setPageDetail(data);
      getClientList(data?.dsp_id);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };
  const getClientList = async dspId => {
    try {
      setLoadingPage(true);
      const res = await axios.get(`${hardBaseUrl}/master/all-client`, {
        headers,
      });
      const { data } = res?.data;
      const selectedDSP = data?.find(item => item?.dsp_id === dspId);
      setCurrencyPrefix(
        selectedDSP?.dsp_currency?.currency_symbol_code || "Rp"
      );
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };

  useEffect(() => {
    getDetail();
  }, []);

  return (
    <Page className={classes.root} title="Advance Transaction">
      {loadingPage ? (
        <SkeletonComponent variant="wave" />
      ) : (
        <Container maxWidth={false}>
          <HeaderTitle
            title="Advance Transaction"
            breadcrumbData={breadcrumbData}
            backButton
          />
          <Divider className={classes.divider} />
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <SectionLabel
                title="Detail Credit Note"
                subTitle="This is detail credit note information, in advance transaction"
              />
            </Grid>
            <Grid item>
              <StatusChip
                label={textCapitalization(pageDetail?.status)}
                type={
                  pageDetail?.status === "paid"
                    ? "success"
                    : pageDetail?.status === "on process"
                    ? "warning"
                    : pageDetail?.status === "error"
                    ? "danger"
                    : ""
                }
              />
            </Grid>
          </Grid>
          <FormCard ref={inputRef}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <InnoImage
                  src={pageDetail?.publisher?.profile_image}
                  alt="img"
                  fill={true}
                  styleImage={{
                    maxWidth: "155.52px",
                    width: "100%",
                    height: "100%",
                    maxHeight: "50px",
                    objectFit: "contain",
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography textAlign="right" fontSize={14} fontWeight={600}>
                  {pageDetail?.publisher.name}
                </Typography>
                <Typography textAlign="right" fontSize={14} color="#6F6F84">
                  {pageDetail?.publisher?.address}
                </Typography>
              </Grid>
            </Grid>
            <Divider className={classes.divider} />
            <Grid container spacing={1}>
              {mockFieldForm?.map(({ title, key, blank }, index) => (
                <Grid
                  item
                  xs={12}
                  md={6}
                  key={index}
                  display="flex"
                  justifyContent={index % 2 !== 0 && "right"}
                >
                  {!blank && (
                    <Box>
                      <Typography
                        className={classes.fieldTitle}
                        textAlign={index % 2 !== 0 && "right"}
                      >
                        {title} :
                      </Typography>
                      <Typography
                        className={classes.fieldValue}
                        textAlign={index % 2 !== 0 && "right"}
                      >
                        {key === "type"
                          ? textCapitalization(pageDetail?.[key])
                          : pageDetail?.[key]}
                      </Typography>
                    </Box>
                  )}
                </Grid>
              ))}
            </Grid>
            <Divider className={classes.divider} />
            <FormLabel label="Song Detail" />
            <CustomTable
              columnTable={columnTable}
              data={pageDetail?.songs || []}
            />
            <Grid container justifyContent="right" mt="16px">
              <Grid item>
                <Typography>Recoupment Total :</Typography>
                <Typography textAlign="right">
                  <CurrencyDisplay
                    value={pageDetail?.revenue}
                    decimalScale={2}
                    prefix={`${currencyPrefix} `}
                  />
                </Typography>
              </Grid>
            </Grid>
          </FormCard>
          <Grid container justifyContent="right">
            {pageDetail?.type === "flat" && pageDetail?.status === "paid" && (
              <PrimaryButton
                onClick={handlePrintPDF}
                loading={loadingButton}
                disabled={loadingButton}
                label={loadingButton ? "Generating PDF..." : "Download As PDF"}
              />
            )}
          </Grid>
        </Container>
      )}
    </Page>
  );
};
const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Transaction",
    link: "/admin/advance",
  },
  {
    label: "Advance Transaction",
    link: "/admin/advance",
  },
  {
    label: "Detail Transaction",
    active: true,
  },
];
const mockFieldForm = [
  { title: "Advance DSP", key: "dsp_name" },
  { title: "Transaction Number", key: "transaction_number" },
  { blank: true },
  { title: "Type", key: "type" },
];
const columnTable = [
  {
    name: "song_title",
    title: "Song",
  },
  {
    name: "composer_names",
    title: "Composer/Author",
    renderText: item => <ArrayChip value={item} />,
  },
  {
    name: "traffic",
    title: "Traffic",
  },
  {
    name: "revenue",
    title: "Revenue",
    headerAlign: "right",
    renderText: item => <CurrencyDisplay value={item} />,
  },
];

export default DetailAdvancedFinance;
